import { ConsolidatedOrderType, OrderType } from "../orderSelectionTypes";

const isSubstring = (str: string, search: string) => {
  if (!search) return true;
  return str.toLowerCase().includes(search.toLowerCase());
};

const parseDate = (dateString: string) => {
  const parsedDate = new Date(dateString);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const filterData = (data: any, filters: any) => {
  // If no filters are applied, return original data
  if (Object.keys(filters).length === 0) return data;
  let filteredData = data;

  const {
    payment_location = [],
    dangerous_goods = [],
    requested_pickup_date = {},
    requested_delivery_date = {},
    pickup_address = [],
    pickup_city = [],
    stop1_city = [],
    stop1_address = [],
    created_by = "",
  } = filters;

  // Separate keys by whether data should be filtered per order or per consolidation (main parent)
  const consolidationFilterKeys = ["pickup_address", "pickup_city", "stop1_city", "stop1_address"];
  const orderFilterKeys = [
    "created_by",
    "payment_location",
    "dangerous_goods",
    "requested_pickup_date",
    "requested_delivery_date",
  ];

  const hasConsolidationFilters = Object.keys(filters).some((key) => consolidationFilterKeys.includes(key));
  const hasOrderFilters = Object.keys(filters).some((key) => orderFilterKeys.includes(key));

  const fromPickupDate = requested_pickup_date.from ? parseDate(requested_pickup_date.from) : null;
  const toPickupDate = requested_pickup_date.to ? parseDate(requested_pickup_date.to) : null;
  const fromDeliveryDate = requested_delivery_date.from ? parseDate(requested_delivery_date.from) : null;
  const toDeliveryDate = requested_delivery_date.to ? parseDate(requested_delivery_date.to) : null;

  // if hasConsolidationFilters, filter the parents out
  if (hasConsolidationFilters) {
    filteredData = filteredData.filter((consolidation: ConsolidatedOrderType) => {
      const matchesPickupAddress = pickup_address.length === 0 || pickup_address.includes(consolidation.pickup_address);
      const matchesPickupCity = pickup_city.length === 0 || pickup_city.includes(consolidation.pickup_city);
      const matchesStop1Address = stop1_address.length === 0 || stop1_address.includes(consolidation.delivery_address);
      const matchesStop1City = stop1_city.length === 0 || stop1_city.includes(consolidation.delivery_city);

      return matchesPickupAddress && matchesPickupCity && matchesStop1City && matchesStop1Address;
    });
  }

  // Remove all orders within the consolidation that do not match the order filters
  if (hasOrderFilters) {
    filteredData = filteredData.map((consolidation: ConsolidatedOrderType) => {
      // Filter the orders within each consolidation
      const filteredOrders = consolidation.orders.filter((order: OrderType) => {
        const matchesCreatedBy = !created_by || isSubstring(order.created_by, created_by);
        const matchesDangerousGood = dangerous_goods.length === 0 || dangerous_goods.includes(order.dangerous_good);
        const matchesPaymentLocation = payment_location.length === 0 || payment_location.includes(order.payment_location);

        // Check date ranges
        const pickupStartDate = new Date(order.pickup_start_date);
        const deliveryStartDate = new Date(order.delivery_start_date);

        const matchesPickupDate =
          !fromPickupDate || !toPickupDate || (pickupStartDate >= fromPickupDate && pickupStartDate <= toPickupDate);

        const matchesDeliveryDate =
          !fromDeliveryDate ||
          !toDeliveryDate ||
          (deliveryStartDate >= fromDeliveryDate && deliveryStartDate <= toDeliveryDate);

        return (
          matchesCreatedBy && matchesDangerousGood && matchesPaymentLocation && matchesPickupDate && matchesDeliveryDate
        );
      });

      // Return the consolidation object with filtered orders
      return {
        ...consolidation,
        orders: filteredOrders,
      };
    });
  }

  // Remove parents if the orders inside them are an empty array
  filteredData = filteredData.filter((consolidation: ConsolidatedOrderType) => consolidation.orders.length > 0);

  return filteredData;
};

const filterSearchData = (data: ConsolidatedOrderType[], searchInput: string) => {
  if (!searchInput) return data;

  const lowercasedInput = searchInput.toLowerCase();

  return data
    .map((consolidatedOrder) => {
      // Check if the consolidatedOrder matches the address
      const matchesAddress =
        consolidatedOrder.pickup_address.toLowerCase().includes(lowercasedInput) ||
        consolidatedOrder.delivery_address.toLowerCase().includes(lowercasedInput);

      if (matchesAddress) return consolidatedOrder;

      // If address doesn't match, filter orders based on reference_number
      const filteredOrders = consolidatedOrder.orders.filter((order) =>
        order.reference_number.toLowerCase().includes(lowercasedInput),
      );

      // If there are no matching orders and address didn't match, exclude the consolidatedOrder
      if (filteredOrders.length === 0) return null;

      // Include consolidatedOrder with filtered orders
      return {
        ...consolidatedOrder,
        orders: filteredOrders,
      };
    })
    .filter((consolidatedOrder) => consolidatedOrder !== null);
};

export { filterData, filterSearchData };
